import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock, X } from 'lucide-react';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MINUTES = Array.from({ length: 60 }, (_, m) => m);

const pad = (n) => String(n).padStart(2, '0');

// Keeps the same "YYYY-MM-DDTHH:mm" shape a native datetime-local input uses,
// so this drop-in replacement doesn't change what the rest of the form stores.
const toValue = (date, hour, minute) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(hour)}:${pad(minute)}`;

const parseValue = (value) => {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [h, min] = (timePart || '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return { date: new Date(y, m - 1, d), hour: h, minute: min };
};

const formatDisplay = (value) => {
  const parsed = parseValue(value);
  if (!parsed) return '';
  const { date, hour, minute } = parsed;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()].slice(0, 3)} ${date.getFullYear()}, ${h12}:${pad(minute)} ${ampm}`;
};

const getCurrentExactTime = () => {
  const now = new Date();
  return { hour: now.getHours(), minute: now.getMinutes() };
};

const DateTimePicker = ({ value, onChange, placeholder = 'Select date & time' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const parsed = parseValue(value);
  const initialTime = getCurrentExactTime();
  const [viewMonth, setViewMonth] = useState(() => parsed?.date || new Date());
  const [hour, setHour] = useState(parsed?.hour ?? initialTime.hour);
  const [minute, setMinute] = useState(parsed?.minute ?? initialTime.minute);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync state if value prop changes
  useEffect(() => {
    if (parsed) {
      setHour(parsed.hour);
      setMinute(parsed.minute);
      setViewMonth(parsed.date);
    }
  }, [value]);

  const handleOpen = () => {
    if (!isOpen) {
      if (!value) {
        const exact = getCurrentExactTime();
        setHour(exact.hour);
        setMinute(exact.minute);
        setViewMonth(new Date());
      }
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const now = new Date();
  const isCurrentMonthOrEarlier =
    viewMonth.getFullYear() < now.getFullYear() ||
    (viewMonth.getFullYear() === now.getFullYear() && viewMonth.getMonth() <= now.getMonth());

  const handlePrevMonth = () => {
    if (isCurrentMonthOrEarlier) return;
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  };

  const getTodayMidnight = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const isPastDay = (day) => {
    if (!day) return false;
    const dayDate = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day);
    dayDate.setHours(0, 0, 0, 0);
    return dayDate < getTodayMidnight();
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return (
      today.getFullYear() === viewMonth.getFullYear() &&
      today.getMonth() === viewMonth.getMonth() &&
      today.getDate() === day
    );
  };

  const isTodaySelected = () => {
    const targetDate = parsed?.date;
    if (!targetDate) return false;
    const today = new Date();
    return (
      today.getFullYear() === targetDate.getFullYear() &&
      today.getMonth() === targetDate.getMonth() &&
      today.getDate() === targetDate.getDate()
    );
  };

  const dayCells = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const startWeekday = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const cells = Array.from({ length: startWeekday }, () => null);
    for (let d = 1; d <= totalDays; d++) cells.push(d);
    return cells;
  }, [viewMonth]);

  const selectDay = (day) => {
    if (!day || isPastDay(day)) return;
    const newDate = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day);

    let nextHour = hour;
    let nextMinute = minute;

    if (isToday(day)) {
      const current = new Date();
      // If no value set or if chosen time is in the past, default to exact current time
      if (!parsed || nextHour < current.getHours() || (nextHour === current.getHours() && nextMinute < current.getMinutes())) {
        nextHour = current.getHours();
        nextMinute = current.getMinutes();
        setHour(nextHour);
        setMinute(nextMinute);
      }
    }

    onChange(toValue(newDate, nextHour, nextMinute));
  };

  const handleTimeChange = (nextHour, nextMinute) => {
    const selectedDate = parsed?.date || (isCurrentMonthOrEarlier ? new Date() : viewMonth);
    const target = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), nextHour, nextMinute);
    const minAllowed = new Date(Date.now() - 30 * 1000); // 30 sec grace for minute flip
    if (target < minAllowed) {
      return;
    }
    setHour(nextHour);
    setMinute(nextMinute);
    onChange(toValue(selectedDate, nextHour, nextMinute));
  };

  const isSelectedDay = (day) => !!day && !!parsed &&
    parsed.date.getFullYear() === viewMonth.getFullYear() &&
    parsed.date.getMonth() === viewMonth.getMonth() &&
    parsed.date.getDate() === day;

  const currentNow = new Date();
  const isTodayActive = isTodaySelected();
  const currentHour = currentNow.getHours();
  const currentMin = currentNow.getMinutes();

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleOpen}
        className="w-full flex items-center justify-between h-9 sm:h-10 px-3 rounded-md border border-input bg-background text-xs sm:text-sm text-left focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
          <span className={`truncate ${value ? 'text-gray-900 font-medium' : 'text-muted-foreground'}`}>
            {value ? formatDisplay(value) : placeholder}
          </span>
        </div>
        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            className="p-0.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            title="Clear schedule time"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-72 bg-white rounded-xl border border-gray-200 shadow-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              disabled={isCurrentMonthOrEarlier}
              onClick={handlePrevMonth}
              className={`p-1 rounded transition-colors ${
                isCurrentMonthOrEarlier
                  ? 'text-gray-300 cursor-not-allowed opacity-40'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
              title={isCurrentMonthOrEarlier ? 'Cannot navigate to past months' : 'Previous month'}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-gray-900">
              {MONTH_NAMES[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </span>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map((d) => (
              <div key={d} className="text-[10px] font-semibold text-gray-400 text-center py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {dayCells.map((day, idx) => {
              const isPast = isPastDay(day);
              const isSelected = isSelectedDay(day);
              const today = isToday(day);

              return (
                <button
                  type="button"
                  key={idx}
                  disabled={!day || isPast}
                  onClick={() => selectDay(day)}
                  className={`h-8 rounded-md text-xs font-medium transition-colors ${
                    !day ? 'invisible' :
                    isPast ? 'text-gray-300 cursor-not-allowed bg-gray-50/50' :
                    isSelected ? 'bg-primary text-primary-foreground font-semibold shadow-sm' :
                    today ? 'bg-primary/10 text-primary font-semibold border border-primary/30' :
                    'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5 flex-1">
              <Clock className="w-4 h-4 text-gray-400 shrink-0" />
              <select
                value={hour}
                onChange={(e) => handleTimeChange(Number(e.target.value), minute)}
                className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {Array.from({ length: 24 }, (_, h) => {
                  const isPast = isTodayActive && h < currentHour;
                  return (
                    <option key={h} value={h} disabled={isPast}>
                      {pad(h)}
                    </option>
                  );
                })}
              </select>
              <span className="text-gray-400 text-xs">:</span>
              <select
                value={minute}
                onChange={(e) => handleTimeChange(hour, Number(e.target.value))}
                className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {MINUTES.map((m) => {
                  const isPast = isTodayActive && (hour < currentHour || (hour === currentHour && m < currentMin));
                  return (
                    <option key={m} value={m} disabled={isPast}>
                      {pad(m)}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="flex items-center gap-2 pl-2">
              {value && (
                <button
                  type="button"
                  onClick={() => {
                    onChange('');
                    setIsOpen(false);
                  }}
                  className="text-xs font-medium text-red-500 hover:text-red-700 hover:underline"
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-xs font-semibold text-primary hover:underline"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
